import { useCallback, useEffect, useState } from 'react';
import { Toolbar } from './components/Toolbar';
import { ObjectList } from './components/ObjectList';
import { SettingsPanel } from './components/SettingsPanel';
import { PlateViewer } from './scene/PlateViewer';
import { useAppStore, store } from './store/appStore';
import { loadMeshFile } from './import/loaders';
import { ToastHost } from './ui/ToastHost';
import { toast } from './ui/toast';

export default function App() {
  const settingsOpen = useAppStore((s) => s.settingsOpen);
  const settingsPanelVisible = useAppStore((s) => s.settingsPanelVisible);
  const objectsPanelVisible = useAppStore((s) => s.objectsPanelVisible);
  const theme = useAppStore((s) => s.theme);
  const [dragging, setDragging] = useState(false);

  const ingestFiles = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files || []);
    if (!list.length) return 0;
    let n = 0;
    for (const f of list) {
      try {
        const mesh = await loadMeshFile(f);
        store.addMesh(mesh.name, mesh.positions);
        n += 1;
      } catch (e) {
        console.error(e);
        toast(`Failed: ${(e as Error).message}`);
      }
    }
    return n;
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      let mode = theme;
      if (theme === 'auto') {
        mode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      root.setAttribute('data-theme', mode);
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute('content', mode === 'dark' ? '#0b0e14' : '#e8ecef');
    };
    apply();
    if (theme !== 'auto') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      setDragging(true);
    };
    const onDragLeave = (e: DragEvent) => {
      if (e.target === document.documentElement || e.target === document.body) setDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (e.dataTransfer?.files?.length) void ingestFiles(e.dataTransfer.files);
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [ingestFiles]);

  // Share Target / launchQueue — only ingest when files are present (never toast "Loaded 0")
  useEffect(() => {
    const lq = (window as unknown as {
      launchQueue?: { setConsumer: (cb: (d: { files?: File[] }) => void) => void };
    }).launchQueue;
    lq?.setConsumer(async (entry) => {
      const files = entry.files || [];
      if (!files.length) return;
      const n = await ingestFiles(files);
      if (n > 0) toast(`Loaded ${n} file${n === 1 ? '' : 's'}`);
    });
  }, [ingestFiles]);

  const appClass = [
    'app',
    !objectsPanelVisible ? 'hide-objects' : '',
    !settingsPanelVisible ? 'hide-settings' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={appClass} data-testid="app">
      <ToastHost />
      <Toolbar
        onFiles={async (files) => {
          const n = await ingestFiles(files);
          if (n > 0) toast(`Loaded ${n} file${n === 1 ? '' : 's'}`);
        }}
      />
      <div className="workspace" data-testid="workspace">
        <div className="object-list-wrap" data-testid="object-list-wrap">
          <ObjectList />
        </div>
        <main className="viewport" data-testid="viewport">
          <PlateViewer />
          {dragging && <div className="drop-overlay">Drop STL / 3MF / OBJ</div>}
        </main>
        <div
          className={`settings-wrap ${settingsOpen ? 'open' : ''}`}
          data-testid="settings-wrap"
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <div
            className="sheet-handle"
            role="button"
            aria-label="Toggle settings sheet"
            onClick={() => store.set({ settingsOpen: !store.getSnapshot().settingsOpen })}
          />
          <SettingsPanel />
        </div>
      </div>
    </div>
  );
}
